import type { Consent } from '@medplum/fhirtypes';
import { onboardingConsentCategoryCode } from './constants';

/** Identifies the consent document a questionnaire item refers to, and its per-practice variants. */
export const CONSENT_DOCUMENT_IDENTIFIER_SYSTEM = 'https://liza.health/fhir/identifier/consent-document';

/**
 * Version of a consent document (`<identifier value>-v<n>`). Stamped onto every `Consent` a patient
 * signs, because the `DocumentReference` is upserted in place on deploy — its resource id alone does
 * not say which text the patient agreed to.
 */
export const CONSENT_DOCUMENT_VERSION_IDENTIFIER_SYSTEM = `${CONSENT_DOCUMENT_IDENTIFIER_SYSTEM}/version`;

/** Records which practice an org-specific consent document variant belongs to. */
export const CONSENT_DOCUMENT_ORGANIZATION_IDENTIFIER_SYSTEM = `${CONSENT_DOCUMENT_IDENTIFIER_SYSTEM}/organization`;

export const ONBOARDING_CONSENT_CATEGORY_SYSTEM = 'https://liza.health/fhir/codesystem/onboarding-consent-category';
export const ONBOARDING_CONSENT_SCOPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/consentscope';
export const ONBOARDING_CONSENT_POLICY_URI = 'https://liza.health/fhir/policy/onboarding-consent';

/** The document a patient actually read, as returned by `onboarding.getConsentDocument`. */
export type SignedConsentDocument = {
  documentReferenceId: string | undefined;
  /** Value of the document's version identifier, e.g. `consent-ephi-v2`. */
  version: string | undefined;
};

/**
 * The `Consent` recorded when a patient acknowledges one onboarding consent. Shared by the
 * patient-facing submit path, the re-consent path, and the seeding helpers so all three record the
 * same shape — re-consent detection reads these back, and a second builder that drifted would read
 * as "this patient never consented".
 */
export const buildOnboardingConsent = (
  linkId: string,
  signedDocument?: SignedConsentDocument,
): Omit<Consent, 'resourceType' | 'patient' | 'dateTime'> => ({
  status: 'active',
  scope: { coding: [{ system: ONBOARDING_CONSENT_SCOPE_SYSTEM, code: 'treatment' }] },
  category: [{ coding: [{ system: ONBOARDING_CONSENT_CATEGORY_SYSTEM, code: onboardingConsentCategoryCode(linkId) }] }],
  policy: [{ uri: ONBOARDING_CONSENT_POLICY_URI }],
  provision: { type: 'permit' },
  ...(signedDocument?.version
    ? { identifier: [{ system: CONSENT_DOCUMENT_VERSION_IDENTIFIER_SYSTEM, value: signedDocument.version }] }
    : {}),
  ...(signedDocument?.documentReferenceId
    ? { sourceReference: { reference: `DocumentReference/${signedDocument.documentReferenceId}` } }
    : {}),
});

/** Onboarding-consent category codes granted by these `Consent` resources. */
export const grantedOnboardingConsentCategoryCodes = (consents: Consent[]): Set<string> =>
  new Set(
    consents
      .filter((consent) => consent.status === 'active')
      .flatMap((consent) => consent.category ?? [])
      .flatMap((category) => category.coding ?? [])
      .filter((coding) => coding.system === ONBOARDING_CONSENT_CATEGORY_SYSTEM)
      .flatMap((coding) => (coding.code ? [coding.code] : [])),
  );

/**
 * Consents the practice now requires that this patient has never granted.
 *
 * Compares by category code rather than by resource, so a patient who consented before a consent was
 * added to their practice's set comes back with exactly the added ones — which is both what gates
 * them into re-consent and what the explanation view lists.
 */
export const missingOnboardingConsentLinkIds = (
  requiredConsentLinkIds: readonly string[],
  grantedConsents: Consent[],
): string[] => {
  const granted = grantedOnboardingConsentCategoryCodes(grantedConsents);
  return requiredConsentLinkIds.filter((linkId) => !granted.has(onboardingConsentCategoryCode(linkId)));
};
